import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as UUID } from 'uuid';
import type { LibrariesState } from './LibkeyLibrarySection';
import { LibraryPickerStatus } from './LibraryPickerStatus';
import {
  LibkeySaveError,
  LibkeySelection,
  saveLibkeySelection,
} from 'query/study/LibKey/Libraries';
import { Button, ComboBox, FormLabel, Icon, IconList } from 'components/core';
import { GrowItem, Inline, Stack } from 'components/layout';

export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

const SAVE_ERROR_GENERIC = `Couldn't save your selection. Try selecting again.`;

export type LibraryPickerProps = {
  initialSelection: LibkeySelection | null;
  saveSelection: typeof saveLibkeySelection;
  librariesState: LibrariesState;
  onRetryLibraries: () => void;
};

const LibraryPicker = ({
  initialSelection,
  saveSelection,
  librariesState,
  onRetryLibraries,
}: LibraryPickerProps) => {
  const inputId = useMemo(() => UUID(), []);
  const helperId = useMemo(() => UUID(), []);
  const inputRef = useRef<HTMLInputElement>(null);
  // Set when the user clears via the X button so we can restore focus to the
  // picker once the save begins (see the layout effect below).
  const restoreFocusOnSaveRef = useRef(false);
  const [selection, setSelection] = useState<LibkeySelection | null>(
    initialSelection
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: 'idle' });
  const isSaving = saveStatus.kind === 'saving';

  const persist = useCallback(
    async (libraryId: number | null) => {
      setSaveStatus({ kind: 'saving' });
      try {
        const saved = await saveSelection(libraryId);
        const next = saved.library_id ? saved : null;
        setSelection(next);
        setSaveStatus({ kind: 'saved' });
      } catch (error) {
        const message =
          error instanceof LibkeySaveError && error.serverMessage
            ? `Couldn't save your selection. ${error.serverMessage}. Try selecting again.`
            : SAVE_ERROR_GENERIC;
        setSaveStatus({ kind: 'error', message });
      }
    },
    [saveSelection]
  );

  const selectionLabel = (() => {
    if (!selection?.library_id) return '';
    if (librariesState.status === 'loaded') {
      const known = librariesState.labelByLibrary.get(selection.library_id);
      if (known) return known;
    }
    return selection.library_name ?? '';
  })();

  const handleChange = (value: string) => {
    if (isSaving) return;
    if (librariesState.status !== 'loaded') return;
    const match = librariesState.libraryByLabel.get(value);
    if (!match) return;
    if (selection?.library_id === match.id) return;
    void persist(match.id);
  };

  const handleClear = () => {
    restoreFocusOnSaveRef.current = true;
    void persist(null);
  };

  // The clear button unmounts as soon as `isSaving` flips true, which would
  // drop focus to <body>. Once that save-triggered render commits, move focus
  // back to the picker input so keyboard users keep their place. Doing it here
  // (after the re-render, when the picker is readOnly) means the dropdown stays
  // shut — focusing the input while it's interactive would auto-open the list.
  // useLayoutEffect runs before paint, so focus never visibly lands on <body>.
  useLayoutEffect(() => {
    if (isSaving && restoreFocusOnSaveRef.current) {
      restoreFocusOnSaveRef.current = false;
      inputRef.current?.focus();
    }
  }, [isSaving]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isSaving) return;
    const value = event.currentTarget.value.trim();
    if (value === '' && selection?.library_id != null) {
      void persist(null);
    }
    // ComboBox itself reverts non-matching typed text back to `value` on blur.
  };

  const allLabels =
    librariesState.status === 'loaded'
      ? Array.from(librariesState.libraryByLabel.keys())
      : [];

  const librariesFailed =
    librariesState.status === 'empty' || librariesState.status === 'error';
  const isInvalid = librariesFailed || saveStatus.kind === 'error';
  const isPickerDisabled = librariesFailed || isSaving;

  return (
    <Stack spacing={1}>
      <FormLabel htmlFor={inputId} isBold>
        Select library
      </FormLabel>
      <Inline alignY="center" spacing={2} shouldWrap={false}>
        <GrowItem>
          <ComboBox
            ref={inputRef}
            id={inputId}
            aria-describedby={helperId}
            aria-invalid={isInvalid || undefined}
            value={selectionLabel}
            data={allLabels}
            enableFiltering
            placeholder="Search libraries..."
            readOnly={isPickerDisabled}
            onBlur={handleBlur}
            onChange={(value) => handleChange(value)}
          />
        </GrowItem>
        {selection && !isSaving && (
          <Button
            size="sm"
            type="neutral"
            variant="outline"
            iconOnly
            onClick={handleClear}
            aria-label={
              selectionLabel
                ? `Remove ${selectionLabel} as your LibKey library`
                : 'Remove your LibKey library'
            }
          >
            <Icon icon={IconList.solid.faXmark} />
          </Button>
        )}
      </Inline>
      <LibraryPickerStatus
        helperId={helperId}
        saveStatus={saveStatus}
        librariesState={librariesState}
        onRetryLibraries={onRetryLibraries}
      />
    </Stack>
  );
};

export { LibraryPicker };
